import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import DramaCard from "@/components/feature/DramaCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  dramaGenres,
  dramaYears,
  ratingOptions,
  sortOptions,
  sortDramas,
  type SortKey,
} from "@/utils/media";

const PAGE_SIZE = 8;

export default function DramaCategory() {
  usePageMeta({
    title: "韩剧剧集大全 - 韩剧TV",
    description:
      "韩剧剧集大全收录热播与经典韩剧，支持按类型、年份与评分筛选，覆盖爱情、悬疑、古装、科幻等题材，帮你快速找到想追的剧。",
    keywords: "韩剧剧集,韩剧大全,韩剧推荐,热播韩剧,韩剧排行榜",
  });

  const [genre, setGenre] = useState("全部");
  const [year, setYear] = useState("全部");
  const [rating, setRating] = useState("全部");
  const [sort, setSort] = useState<SortKey>("hot");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = dramas.filter((d) => {
      const okGenre = genre === "全部" || d.genres.includes(genre);
      const okYear = year === "全部" || String(d.year) === year;
      const minRating =
        rating === "9.0以上" ? 9 : rating === "8.5以上" ? 8.5 : rating === "8.0以上" ? 8 : 0;
      return okGenre && okYear && d.rating >= minRating;
    });
    list = sortDramas(list, sort);
    return list;
  }, [genre, year, rating, sort]);

  const shown = filtered.slice(0, visible);

  const update = (fn: () => void) => {
    fn();
    setVisible(PAGE_SIZE);
  };

  const resetFilters = () => {
    setGenre("全部");
    setYear("全部");
    setRating("全部");
    setSort("hot");
    setVisible(PAGE_SIZE);
  };

  const hasFilter = genre !== "全部" || year !== "全部" || rating !== "全部";

  const FilterGroup = ({
    label,
    options,
    value,
    onChange,
  }: {
    label: string;
    options: string[];
    value: string;
    onChange: (v: string) => void;
  }) => (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      <span className="w-16 shrink-0 pt-1.5 text-sm font-500 text-foreground-700">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
              value === opt
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "剧集大全" }]} />

      <header className="mt-6">
        <h1 className="font-heading text-2xl font-800 text-foreground-950 md:text-4xl">韩剧剧集大全</h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          这里汇集了各类热播与经典韩剧，可按题材、年份与评分自由筛选。
          无论你想追一部高甜爱情、烧脑悬疑还是治愈生活剧，都能在这里找到。
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
        {/* 侧边筛选 */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-xl border border-background-200/70 bg-background-50 px-4 py-3 text-sm font-500 text-foreground-800 lg:cursor-default cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-lg"></i> 筛选条件
            </span>
            <i className={`ri-arrow-down-s-line text-xl lg:hidden transition-transform ${filterOpen ? "rotate-180" : ""}`}></i>
          </button>

          <div
            className={`mt-4 space-y-6 rounded-xl border border-background-200/70 bg-background-50 p-5 ${
              filterOpen ? "block" : "hidden lg:block"
            }`}
          >
            <FilterGroup
              label="类型"
              options={dramaGenres}
              value={genre}
              onChange={(v) => update(() => setGenre(v))}
            />
            <div className="h-px w-full bg-background-200/70"></div>
            <FilterGroup
              label="年份"
              options={dramaYears}
              value={year}
              onChange={(v) => update(() => setYear(v))}
            />
            <div className="h-px w-full bg-background-200/70"></div>
            <FilterGroup
              label="评分"
              options={ratingOptions}
              value={rating}
              onChange={(v) => update(() => setRating(v))}
            />
            {hasFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex w-full items-center justify-center gap-1 rounded-md border border-background-300 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-refresh-line"></i> 重置筛选
              </button>
            )}
          </div>
        </aside>

        {/* 列表 */}
        <div>
          <div className="flex flex-col gap-3 border-b border-background-200/70 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-500">
              共 <span className="font-600 text-foreground-800">{filtered.length}</span> 部剧集
            </p>
            <div className="flex items-center gap-1 self-start rounded-full bg-background-100 p-1">
              {sortOptions.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => update(() => setSort(s.key))}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                    sort === s.key ? "bg-background-50 text-primary-600" : "text-foreground-600"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {shown.length > 0 ? (
            <>
              <div className="mt-6 grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-3">
                {shown.map((drama) => (
                  <DramaCard key={drama.id} drama={drama} />
                ))}
              </div>
              {visible < filtered.length && (
                <div className="mt-10 flex flex-col items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-8 py-3 text-sm font-500 text-foreground-800 transition-colors hover:bg-background-100 cursor-pointer"
                  >
                    <i className="ri-add-line text-lg"></i> 加载更多
                  </button>
                  <p className="text-xs text-foreground-500">
                    已显示 {shown.length} / {filtered.length}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 py-20 text-center">
              <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
              <p className="mt-4 text-base font-500 text-foreground-800">没有找到匹配的剧集</p>
              <p className="mt-2 text-sm text-foreground-500">试着放宽筛选条件，或看看我们的编辑精选。</p>
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 cursor-pointer hover:bg-primary-600"
                >
                  重置筛选
                </button>
                <Link
                  to="/"
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 cursor-pointer hover:bg-background-100"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}